import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../mui-components/Layout';
import { Box, Button, Chip, Typography, Alert } from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { GridToolbar } from '@mui/x-data-grid';
import { NoRowsGrid, NoResultsGrid } from '../mui-components/NoRowsGrid';
import { pageTitles } from '../data/pageTitles';
import Loading from '../mui-components/Loading';
import { useGlobalContext } from '../context/GlobalStore';
import apiClient from '../utils/apiClient';
import moment from 'moment';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

export default function WebhookLogs() {
  const { eventType } = useParams();
  const navigate = useNavigate();
  const { state } = useGlobalContext();
  
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Format event type for display
  const eventTypeDisplay = eventType === 'DEVICE_MEASUREMENT' 
    ? 'Device Measurement' 
    : 'Order Updates';

  document.title = `${eventTypeDisplay} Webhook Logs | ${pageTitles.webhooks}`;

  const columns = [
    {
      field: 'created_at',
      headerName: 'Timestamp',
      width: 180,
      type: 'dateTime',
      valueGetter: (value) => value ? new Date(value) : null,
      valueFormatter: (value) => value ? moment(value).format('L, LT') : '',
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 110,
      type: 'singleSelect',
      valueOptions: ['SUCCESS', 'FAILED'],
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={params.value === 'SUCCESS' ? 'success' : 'error'}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      field: 'attempt_number',
      headerName: 'Attempt',
      width: 80,
      type: 'number',
    },
    {
      field: 'http_status',
      headerName: 'HTTP Status',
      width: 110,
      type: 'number',
      valueFormatter: (value) => value || '—',
    },
    {
      field: 'subject_id',
      headerName: eventType === 'DEVICE_MEASUREMENT' ? 'Device ID' : 'Order Number',
      width: 200,
    },
    {
      field: 'webhook_id',
      headerName: 'Event Group',
      width: 160,
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{
            fontFamily: 'monospace',
            fontSize: '0.75rem',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
          title={params.value}
        >
          {params.value || '—'}
        </Typography>
      ),
    },
    {
      field: 'target_url',
      headerName: 'Target URL',
      width: 280,
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{
            fontFamily: 'monospace',
            fontSize: '0.8rem',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
          title={params.value}
        >
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'error_message',
      headerName: 'Error',
      width: 300,
      renderCell: (params) => params.value ? (
        <Typography
          variant="body2"
          color="error"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
          title={params.value}
        >
          {params.value}
        </Typography>
      ) : '—',
    },
  ];

  useEffect(() => {
    if (!state.apiKey?.key || !state.account?.pylo_accounts) {
      return;
    }
    fetchDeliveries();
  }, [state.apiKey, state.account, eventType]);

  async function fetchDeliveries() {
    setIsLoading(true);
    setError(null);

    try {
      const accountId = state?.account?.pylo_accounts?.find(
        (a) => a.api_key === state.apiKey?.key
      )?.id;

      if (!accountId) {
        setError('No account found for the current API key.');
        setIsLoading(false);
        return;
      }

      const response = await apiClient.getWebhookDeliveries(accountId, eventType);
      const deliveries = response.deliveries || [];
      
      // Add unique id for DataGrid (use delivery_id)
      const rowsWithIds = deliveries.map((d, index) => ({
        ...d,
        id: d.delivery_id || index,
      }));

      setRows(rowsWithIds);
    } catch (err) {
      console.error('Error fetching webhook deliveries:', err);
      setError('Failed to load webhook delivery logs.');
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <Layout title={`${eventTypeDisplay} Webhook Logs`}>
      <Box sx={{ mb: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/webhooks')}
          sx={{ mb: 2 }}
        >
          Back to Webhooks
        </Button>
        
        <Alert severity="info" sx={{ mb: 2 }}>
          Webhook delivery logs are retained for 7 days.
        </Alert>
      </Box>

      <Box sx={{ height: '100%' }}>
        {isLoading ? (
          <Loading />
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <DataGridPro
            rows={rows}
            columns={columns}
            slots={{
              toolbar: GridToolbar,
              noRowsOverlay: NoRowsGrid,
              noResultsOverlay: NoResultsGrid,
            }}
            pagination
            rowGroupingColumnMode="single"
            initialState={{
              rowGrouping: {
                model: ['webhook_id'],
              },
              sorting: {
                sortModel: [{ field: 'created_at', sort: 'desc' }],
              },
            }}
            groupingColDef={{
              headerName: 'Event Group',
              width: 220,
            }}
            defaultGroupingExpansionDepth={-1}
            sx={{
              '& .MuiDataGridPro-overlay': { minHeight: 300 },
              '& .MuiDataGrid-overlayWrapper': { minHeight: 300 },
            }}
          />
        )}
      </Box>
    </Layout>
  );
}
